import { Injectable } from '@angular/core';
import { NotificationSocketService } from './notification-socket.service';
import { NotificationElement, UserAlert } from './notification.model';
import { HealthAppService } from 'src/app/services/explore-health/health-app.service';
import { SelectOrganizationDialogComponent } from 'src/app/components/select-organization-dialog/select-organization-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TenantService } from 'src/app/services/explore-health/tenant.service';
import { Subject, takeUntil } from 'rxjs';
import {
  ConnectionReasons,
  SocketConnectionStage,
} from '../content-generation-canvas/canvas.model';
import { AlertpopupComponent } from 'src/app/components/alertpopup/alertpopup.component';
import { NotificationAlertService } from './notification-alert.service';
import { NotificationApiHandlerService } from './notification-api-handler.service';
import { AuthService } from 'src/app/services/common/auth.service';

@Injectable()
export class NotificationService {
  public haveUndreadNotification: boolean = false;
  private destroyNotifier$!: Subject<void>;
  public get userAlertSettings(): UserAlert {
    return this.auth.userAlertSettings;
  }

  constructor(
    private readonly socket: NotificationSocketService,
    private readonly haService: HealthAppService,
    private readonly auth: AuthService,
    private readonly dialog: MatDialog,
    private readonly tenantService: TenantService,
    private readonly notificationAlertService: NotificationAlertService,
    private readonly notificationApiHandler: NotificationApiHandlerService
  ) {
    this.tenantService.onLogoutSubject$.subscribe(() => {
      this.destroySocket();
      this.destroyNotifier$?.next();
      this.destroyNotifier$?.complete();
    });
  }

  createSocketInstance(isFromLogin: boolean = false) {
    this.getUserAlertSettings(isFromLogin);

    if (this.socket.isSocketCreated()) {
      return;
    }
    this.destroyNotifier$?.next();
    this.destroyNotifier$?.complete();
    this.destroyNotifier$ = new Subject<void>();
    this.haService.getChatSocketAccessUrl().subscribe({
      next: (response: any) => {
        if (response?.token_url) {
          this.socket.connect(response.token_url);
          this.socket.eventStream
            .pipe(takeUntil(this.destroyNotifier$))
            .subscribe({
              next: (event) => {
                console.log(
                  `Notification Socket Event [${event.type}]:`,
                  event
                );
                switch (event.type) {
                  case SocketConnectionStage.MESSAGE:
                    if (this.userAlertSettings.receive_in_app_alert) {
                      this.notificationAlertService.updateHeaderNotificationIcon();
                      this.notificationAlertService.showNotification(
                        event.data.payload
                      );
                    }
                    break;
                  case SocketConnectionStage.DISCONNECT:
                    if (event.data.reason != ConnectionReasons.MANUAL) {
                      this.showAlertDialog();
                    }
                    break;
                }
              },
              error: (error) => {
                console.error('WebSocket subscription error:', error);
              },
            });
        }
      },
      error: (error) => {
        console.error('Error fetching socket access URL:', error);
      },
    });
  }

  getUserAlertSettings(isFromLogin: boolean = false) {
    this.notificationApiHandler.getUserAlertSettings().subscribe({
      next: (response) => {
        this.setUserAlertSettings(response);

        if (isFromLogin && response.has_seen_user_guide === false) {
          this.haService.triggerStartUserGuide();
        }

        if (this.userAlertSettings.receive_in_app_alert) {
          this.checkUnreadNotifications();
        }
      },
      error: (error) => {
        console.error('Error fetching user alert settings:', error);
      },
    });
  }

  updateUserAlertSettings(payload: UserAlert) {
    this.notificationApiHandler.updateUserAlertSettings(payload).subscribe({
      next: (response) => {
        this.setUserAlertSettings(response);
      },
      error: (error) => {
        console.error('Error updating user alert settings:', error);
      },
    });
  }

  setUserAlertSettings(response: UserAlert) {
    if (!response) {
      response = {
        receive_email_alert: true,
        receive_in_app_alert: true,
        has_seen_user_guide: false,
      };
    }
    this.auth.userAlertSettings = response;
  }

  checkUnreadNotifications() {
    this.notificationApiHandler.getTopUnreadNotifications().subscribe({
      next: (response) => {
        if (response?.notifications?.length > 0) {
          this.notificationAlertService.updateHeaderNotificationIcon();
        }
      },
      error: (error) => {
        console.error('Error fetching notifications:', error);
      },
    });
  }

  /**
   * Displays an alert dialog to the user.
   * This method can be customized to show specific messages or actions.
   */
  showAlertDialog() {
    let dialogRef = this.dialog.open(AlertpopupComponent, {
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false,
      width: '50vw',
      backdropClass: 'backdropBackground',
      data: {
        title: 'Connection Lost',
        message: `We've lost connection to the server. Please check your network and try reconnecting to restore real-time updates.`,
        postitionButtonMessage: 'Reconnect Now',
        negativeButtonMessage: 'Close',
      },
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.restartAppNotificationSocket();
      }
    });
  }

  /**
   * Restarts the notification socket connection after a delay.
   * This is useful when the socket connection is lost or needs to be re-established.
   */
  restartAppNotificationSocket() {
    this.destroySocket();
    setTimeout(() => {
      this.createSocketInstance();
    }, 2000);
  }

  /**
   * Formats a datetime string into a human-readable format.
   * @param datetime - The datetime string to format.
   * @returns A formatted string (e.g., "Today", "Yesterday", or "DD MMM, YYYY").
   */
  formatDateTime(datetime: string): string {
    if (!datetime) return 'Today';

    const itemDate = new Date(datetime);
    const today = new Date();
    const startOfToday = new Date(today.setHours(0, 0, 0, 0));
    const startOfItemDate = new Date(itemDate.setHours(0, 0, 0, 0));

    const diffMs = startOfToday.getTime() - startOfItemDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const isSameYear =
      startOfItemDate.getFullYear() === startOfToday.getFullYear();

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';

    const todayDay = startOfToday.getDay(); // 0 (Sun) to 6 (Sat)
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - todayDay);

    if (startOfItemDate >= startOfWeek) {
      return startOfItemDate.toLocaleDateString(undefined, { weekday: 'long' });
    }

    const lastWeekStart = new Date(startOfWeek);
    lastWeekStart.setDate(startOfWeek.getDate() - 7);

    if (startOfItemDate >= lastWeekStart && startOfItemDate < startOfWeek) {
      return 'Previous week';
    }

    if (isSameYear) {
      return startOfItemDate.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      });
    }

    return startOfItemDate.toLocaleDateString(undefined, { year: 'numeric' });
  }

  processNotifications(notifications: NotificationElement[]) {
    notifications?.forEach((notification: NotificationElement) => {
      notification.displayDate = this.formatDateTime(notification.created_at);
    });
  }

  isSameOrganization(orgId: number) {
    if (Number(this.haService.auth.getOrgId()) === orgId) {
      return true;
    }
    let organization = this.findOrganizationName(orgId);
    if (organization) {
      this.haService
        .showConfirmationAlert(
          `Switch to ${organization.orgName ?? organization.name} ?`,
          'You are trying to access a notification from a different organization. Do you want to switch to that organization?',
          false
        )
        .then((result) => {
          if (result) this.updateOrganization();
        });
    } else {
      this.haService.showConfirmationAlert(
        `Organization not found`,
        'The organization you are trying to access does not exist or is not available.'
      );
    }
    return false;
  }

  updateOrganization() {
    let ref = this.dialog.open(SelectOrganizationDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      autoFocus: false,
      backdropClass: 'backdropBackground',
      panelClass: 'notification-dialog',
      data: this.haService.auth.getTenants() ?? [],
    });
    ref.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        this.tenantService.updateTokens();
      }
    });
  }

  findOrganizationName(orgId: number) {
    return (
      this.haService.auth
        .getTenants()
        ?.find((org: any) => org.orgId === orgId) ?? null
    );
  }

  openNotification(notification: NotificationElement) {
    switch (notification.source_type) {
      case 'CHAT':
        if (notification.metadata?.id && notification.metadata?.org_id) {
          if (this.isSameOrganization(Number(notification.metadata.org_id))) {
            this.openChat(notification.metadata.id);
          }
        }
        break;
      case 'DATASET':
        break;
      default:
        break;
    }
  }

  openChat(id: string) {
    this.haService.openDocumentCanvas();
  }

  destroySocket() {
    this.socket.forceClearConnection();
  }

  ngOnDestroy(): void {
    this.destroyNotifier$?.next();
    this.destroyNotifier$?.complete();
    this.destroySocket();
  }
}
